/**
 * MQTT-based WebRTC signaling for desktop VNC bridge.
 *
 * Topic layout (identical to device-agent):
 *   Publish:   `{topic}/signaling/request`
 *   Subscribe: `{topic}/signaling/response`
 */
import mqtt from "mqtt";
const INITIAL_RECONNECT_MS = 1000;
const MAX_RECONNECT_MS = 30000;
export class SignalingError extends Error {
    code;
    meta;
    constructor(code, message, meta) {
        super(message);
        this.name = "SignalingError";
        this.code = code;
        if (meta)
            this.meta = meta;
    }
}
/**
 * Inspect an mqtt.js error to decide whether it represents an
 * MQTT CONNACK rejection caused by **bad credentials** as opposed to
 * a network failure.
 *
 * MQTT 3.1.1 CONNACK return codes:
 *   - 4 → "Connection Refused, bad user name or password"
 *   - 5 → "Connection Refused, not authorized"
 * MQTT 5 CONNACK reason codes (the protocol version we negotiate):
 *   - 0x86 / 134 → "Bad User Name or Password"
 *   - 0x87 / 135 → "Not authorized"
 *   - 0x88 / 136 → "Server unavailable" — NOT auth, treat as network
 *   - 0x8C / 140 → "Bad authentication method"
 *   - 0x8D / 141 → "Keep Alive timeout" — NOT auth
 *
 * Real-world EMQX error shapes seen on the wire:
 *   - `err.code === 5` (mqtt.js wraps MQTT 3.x CONNACK)
 *   - `err.code === 134 | 135 | 140` (mqtt.js MQTT 5 reasonCode field)
 *   - `err.message` containing "Not authorized" / "Bad User Name"
 *
 * We err on the side of **only** classifying as auth_failed when we
 * have explicit evidence — a generic `err.code === undefined` network
 * timeout must NOT trigger a refresh loop.
 */
export function isMqttAuthFailure(err) {
    if (!err || typeof err !== "object")
        return false;
    const anyErr = err;
    if (typeof anyErr.code === "number") {
        if (anyErr.code === 4 || anyErr.code === 5)
            return true;
        if (anyErr.code === 134 || anyErr.code === 135 || anyErr.code === 140)
            return true;
    }
    if (typeof anyErr.reasonCode === "number") {
        if (anyErr.reasonCode === 134 || anyErr.reasonCode === 135 || anyErr.reasonCode === 140) {
            return true;
        }
    }
    if (typeof anyErr.message === "string") {
        const m = anyErr.message.toLowerCase();
        if (m.includes("bad user name") || m.includes("bad username") || m.includes("not authorized")) {
            return true;
        }
    }
    return false;
}
export class SignalingClient {
    client = null;
    requestTopic;
    responseTopic;
    options;
    /**
     * Live MQTT password (the JWT). Stored separately from
     * `options.mqttToken` so `refreshCredentials(newToken)` can swap it
     * for the next reconnect without us having to mutate the original
     * options object the caller still references. mqtt.js's
     * `client.options.password` is what the broker re-uses on
     * `client.reconnect()`, so we update both that and our local copy.
     */
    currentToken;
    reconnectAttempts = 0;
    reconnectTimer = null;
    disposed = false;
    constructor(options) {
        this.options = options;
        this.currentToken = options.mqttToken;
        this.requestTopic = `${options.deviceTopic}/signaling/request`;
        this.responseTopic = `${options.deviceTopic}/signaling/response`;
    }
    get connected() {
        return this.client?.connected ?? false;
    }
    /**
     * Replace the MQTT JWT used by this client. The next
     * `client.reconnect()` (driven by `scheduleReconnect`) will use the
     * new token; if the client is currently connected we leave it
     * alone — the caller is expected to invoke `disconnect()` +
     * `connect()` (or let the existing close-event flow trigger
     * reconnection) once a fresh streamParams object is in hand.
     */
    refreshCredentials(newToken) {
        this.currentToken = newToken;
        if (this.client) {
            // mqtt.js exposes `options` on the client and re-reads
            // `username` / `password` on `reconnect()`. Mutating it is the
            // canonical way to rotate creds without throwing the connection
            // away — see the mqtt-packet protocol-handling code in mqtt.js.
            const opts = this.client.options;
            opts.password = newToken;
        }
        // Reset the backoff counter so a freshly-credentialled retry gets
        // the full attempt budget again, not the tail of a doomed one.
        this.reconnectAttempts = 0;
    }
    async connect() {
        this.disposed = false;
        this.reconnectAttempts = 0;
        return new Promise((resolve, reject) => {
            let resolved = false;
            this.client = mqtt.connect(this.options.mqttUrl, {
                username: "desktop-viewer",
                password: this.currentToken,
                protocolVersion: 5,
                clean: true,
                // This class owns the backoff. mqtt.js otherwise starts a second
                // reconnect timer and can disconnect a freshly connected client.
                reconnectPeriod: 0,
                resubscribe: false,
            });
            this.client.on("connect", () => {
                const client = this.client;
                if (this.disposed || !client)
                    return;
                client.subscribe(this.responseTopic, (err) => {
                    if (this.disposed || this.client !== client)
                        return;
                    if (err) {
                        // A socket that closed between CONNACK and SUBSCRIBE will be
                        // retried by the close handler. It is not a subscription denial.
                        if (client.disconnecting || !client.connected)
                            return;
                        const error = new SignalingError("subscribe_failed", `Failed to subscribe: ${err.message}`, { reason: err.message });
                        if (!resolved) {
                            resolved = true;
                            reject(error);
                        }
                        else {
                            this.options.onError?.(error);
                        }
                    }
                    else {
                        if (!client.connected || client.disconnecting)
                            return;
                        this.reconnectAttempts = 0;
                        this.clearReconnectTimer();
                        if (!resolved) {
                            resolved = true;
                            resolve();
                        }
                    }
                });
            });
            this.client.on("error", (err) => {
                if (this.disposed)
                    return;
                // Translate auth-shaped errors into a typed `auth_failed`
                // signal so `viewer.handleError` can route them through the
                // refresh path instead of dropping straight to `failed`. We
                // re-emit synchronously even after the initial promise has
                // resolved (auth failures can hit on reconnect mid-session).
                const authError = isMqttAuthFailure(err)
                    ? new SignalingError("auth_failed", err instanceof Error ? err.message : String(err), {
                        originalCode: err.code,
                        originalReasonCode: err.reasonCode,
                    })
                    : err;
                if (!resolved) {
                    resolved = true;
                    reject(authError);
                    return;
                }
                // Already-resolved path — surface to the viewer via onError so
                // the refresh machine can re-fetch credentials and re-init.
                if (this.options.onError) {
                    this.options.onError(authError instanceof Error ? authError : new Error(String(authError)));
                }
            });
            this.client.on("close", () => {
                if (this.disposed)
                    return;
                this.scheduleReconnect();
            });
            this.client.on("message", (_topic, payload) => {
                try {
                    const msg = JSON.parse(payload.toString());
                    if (msg.type === "answer" && msg.sdp) {
                        this.options.onAnswer(msg.sdp);
                    }
                    else if (msg.type === "ice" && msg.candidate) {
                        this.options.onIceCandidate?.({
                            candidate: typeof msg.candidate === "string" ? msg.candidate : msg.candidate.candidate,
                            sdpMid: msg.sdpMid ?? msg.candidate?.sdpMid ?? "0",
                            sdpMLineIndex: msg.sdpMLineIndex ?? msg.candidate?.sdpMLineIndex ?? 0,
                        });
                    }
                }
                catch {
                    // ignore malformed messages
                }
            });
        });
    }
    scheduleReconnect() {
        if (this.disposed || this.reconnectTimer)
            return;
        this.reconnectAttempts++;
        const delay = Math.min(INITIAL_RECONNECT_MS * Math.pow(2, this.reconnectAttempts - 1), MAX_RECONNECT_MS);
        this.options.onReconnecting?.(this.reconnectAttempts);
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            if (this.disposed || !this.client)
                return;
            this.client.reconnect();
        }, delay);
    }
    clearReconnectTimer() {
        if (!this.reconnectTimer)
            return;
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
    }
    sendOffer(sdp) {
        if (!this.client?.connected)
            throw new SignalingError("not_connected", "Signaling not connected");
        this.client.publish(this.requestTopic, JSON.stringify({ type: "offer", sdp }));
    }
    sendIceCandidate(candidate) {
        if (!this.client?.connected)
            throw new SignalingError("not_connected", "Signaling not connected");
        this.client.publish(this.requestTopic, JSON.stringify({
            type: "ice",
            candidate: candidate.candidate,
            sdpMid: candidate.sdpMid,
            sdpMLineIndex: candidate.sdpMLineIndex,
        }));
    }
    disconnect() {
        this.disposed = true;
        this.clearReconnectTimer();
        this.client?.end(true);
        this.client = null;
    }
}
//# sourceMappingURL=signaling.js.map